export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface JsonBody {
  [key: string]: unknown;
}

/**
 * Inside the APK? Capacitor's native bridge injects window.Capacitor on every
 * page load; a browser never has it. Detected, never assumed — the same bundle
 * runs as the PWA.
 */
export function runsOnDevice(): boolean {
  const capacitor = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return capacitor?.isNativePlatform?.() === true;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    // In the APK there is no server behind /api: provider-data routes are
    // answered on the device by the same shared code the server runs
    // (Section 22 Phase 1). Loaded on first use and only here, so the web
    // bundle never carries it.
    const onDevice = runsOnDevice() ? await (await import("./deviceApi.js")).answerOnDevice(path, init) : null;
    response =
      onDevice ??
      (await fetch(path, {
        headers: { "content-type": "application/json", ...init?.headers },
        ...init,
      }));
  } catch (error) {
    throw new ApiError(0, "Network request failed.", "network");
  }
  if (!response.ok) {
    let code: string | undefined;
    let message = `Request failed (${response.status}).`;
    try {
      const body = (await response.json()) as JsonBody;
      if (typeof body.error === "string") code = body.error;
      if (typeof body.message === "string") message = body.message;
    } catch {
      // non-JSON error body — keep default message
    }
    throw new ApiError(response.status, message, code);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
};
